import { MatrixEvent } from 'matrix-js-sdk';
import {formatMessagePreview} from "../utils/messageFormatting.ts";
import { isMessageEdit } from '../utils/messageEdits';
import { stripPlainReplyFallback } from '../utils/privateReplyQuote.ts';
import { focusAppWindow } from '../utils/desktopBridge';

export class BrowserNotificationService {
    private hasPermission = false;
    private isActive = true;
    private onNotificationClick?: (roomId: string) => void;

    constructor() {
        this.checkNotificationPermission();
    }

    public setNotificationClickHandler(handler: (roomId: string) => void): void {
        this.onNotificationClick = handler;
    }

    private checkNotificationPermission(): void {
        if (!('Notification' in window)) {
            this.hasPermission = false;
            return;
        }

        this.hasPermission = Notification.permission === 'granted';
    }

    public async requestPermission(): Promise<boolean> {
        if (!('Notification' in window)) {
            return false;
        }

        if (Notification.permission === 'granted') {
            this.hasPermission = true;
            return true;
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            this.hasPermission = permission === 'granted';
            return this.hasPermission;
        }

        return false;
    }

    private showDesktopNotification(title: string, body: string, roomId?: string, icon?: string): void {
        if (!this.hasPermission || !this.isActive) return;

        const notification = new Notification(title, {
            body,
            icon: icon || '/favicon.png',
            badge: '/favicon.png',
            requireInteraction: false,
            silent: false,
            data: { roomId },
        });

        notification.onclick = () => {
            focusAppWindow();


            if (roomId && this.onNotificationClick) {
                this.onNotificationClick(roomId);
            }

            notification.close();
        };
    }

    public showJoinedNotification(data: {
        roomId: string;
        displayName: string;
        roomName: string;
    }): void {
        if (!this.hasPermission || !this.isActive) {
            return;
        }

        const title = `${data.displayName} joined`;
        const body = data.roomName;

        this.showDesktopNotification(
            title,
            body,
            data.roomId
        );
    }
    public showMessageNotification(message: MatrixEvent, senderDisplayName?: string, roomName?: string): void {
        if (isMessageEdit(message)) return;

        const senderId = message.getSender();
        const sender = senderDisplayName || senderId?.split(':')[0].replace('@', '') || 'Unknown';
        const msgContent = message.getContent();
        let description = formatMessagePreview(stripPlainReplyFallback(msgContent.body || 'New message'));

        if (msgContent.msgtype === 'm.audio') {
            description = '🎵 Voice message';
        } else if (msgContent.msgtype === 'm.image') {
            description = '📷 Image';
        } else if (msgContent.msgtype === 'm.video') {
            description = '🎥 Video';
        } else if (msgContent.msgtype === 'm.file') {
            description = `📎 ${msgContent.filename || msgContent.body || 'File'}`;
        }

        const title = roomName ? `${sender} - ${roomName}` : `${sender} - new message`;

        this.showDesktopNotification(
            title,
            description.substring(0, 200),
            message.getRoomId(),
        );
    }

    public showGroupedMessageNotification(messageCount: number, roomInfo: string): void {
        if (!this.hasPermission || !this.isActive) {
            return;
        }

        const title = `You have ${messageCount} new messages`;
        const body = roomInfo;

        this.showDesktopNotification(
            title,
            body,
            undefined,
            '/favicon.png'
        );
    }

    public showBotReadyNotification(type: 'summary' | 'transcript', roomName: string, roomId: string): void {
        const label = type === 'summary' ? 'summary' : 'transcript';
        const title = `AI ${label} ready`;
        const body = `in ${roomName}`;
        this.showDesktopNotification(title, body, roomId);
    }

    public showCallNotification(data: {
        roomId: string;
        title: string;
        body: string;
        tag: string;
    }): () => void {
        if (!this.hasPermission || !this.isActive) {
            return () => undefined;
        }

        const notification = new Notification(data.title, {
            body: data.body,
            icon: '/favicon.png',
            badge: '/favicon.png',
            tag: data.tag,
            requireInteraction: true,
            data: {roomId: data.roomId},
        });

        notification.onclick = () => {
            window.focus();
            if (this.onNotificationClick) {
                this.onNotificationClick(data.roomId);
            }
            notification.close();
        };

        return () => notification.close();
    }

    public showSystemNotification(message: string): void {
        this.showDesktopNotification('PhoneHQ Chat', message);
    }

    public getPermissionStatus(): string {
        if (!('Notification' in window)) return 'unsupported';
        return Notification.permission;
    }
    showRoomInvitationNotification(data: {
        roomId: string;
        inviterName: string;
        roomName: string;
    }): void {
        if (!this.hasPermission || !this.isActive) {
            return;
        }

        const title = `Room Invitation`;
        const body = `${data.inviterName} invited you to new chat`;

        const notification = new Notification(title, {
            body,
            icon: '/favicon.png',
            badge: '/favicon.png',
            tag: `room-invitation-${data.roomId}`,
            data: {
                type: 'room-invitation',
                roomId: data.roomId,
                action: 'view-invitation'
            }
        });
        notification.onclick = () => {
            focusAppWindow();


            if (data.roomId && this.onNotificationClick) {
                this.onNotificationClick(data.roomId);
            }

            notification.close();
        };

    }


}

export const browserNotificationService = new BrowserNotificationService();
